import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { accountRole } from "pages/dashboard/dashboardFeatures";
import { Box, useTheme } from "@mui/material";

import { useMeQuery } from "generated/graphql";
import { useSnack } from "hooks/useSnack";
import Typography from "components/basic/typography";

import { useVendorAvailability } from "../calendar/setAvailability/availabilityApi";
import DeleteBookingDialog from "../calendar/booking/components/DeleteBookingDialog";
import MeetingsSidebar from "./components/MeetingsSidebar";
import StatCards from "./components/StatCards";
import MeetingsTable from "./components/MeetingsTable";
import MeetingDetail from "./components/MeetingDetail";
import MeetingsHeader from "./components/MeetingsHeader";
import MeetingsToolbar from "./components/MeetingsToolbar";
import MeetingsPagination from "./components/MeetingsPagination";
import { countStatuses, matchesDate, meetingDateTime } from "./helpers/filters";
import { exportMeetingsCsv } from "./helpers/exportCsv";
import { ConfirmInput, useMeetings } from "./meetingsApi";
import { DateFilter, Meeting, StatusFilter, TypeFilter } from "./types";

const Meetings: React.FC = () => {
  const theme = useTheme();
  const showSnack = useSnack();
  const { t } = useTranslation("meetings");

  const [{ data }] = useMeQuery();
  const isVendor = accountRole(data?.me) === "vendor";

  const { meetings, confirm, cancel, remove, byEmail } = useMeetings(isVendor);
  const { availability } = useVendorAvailability(isVendor);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState<DateFilter>("all");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deletingMeeting, setDeletingMeeting] = useState<Meeting | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Active (non-cancelled) bookings with effective times — for slot conflicts.
  const activeBookings = useMemo(
    () =>
      meetings
        .filter((m) => m.displayStatus !== "cancelled")
        .map((m) => ({ ...m.booking, date: m.date, from: m.from, to: m.to })),
    [meetings],
  );

  // Filter pipeline: search + date apply to everything; status/type are facets.
  const base = useMemo(() => {
    const q = search.trim().toLowerCase();
    return meetings.filter((m) => {
      if (!matchesDate(m, dateFilter)) {
        return false;
      }
      if (q) {
        const hay = `${m.customerName} ${m.title} ${
          m.estimate?.estimateNumber ?? ""
        }`.toLowerCase();
        if (!hay.includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [meetings, search, dateFilter]);

  const statusCounts = useMemo(
    () =>
      countStatuses(
        base.filter((m) => typeFilter === "all" || m.type === typeFilter),
      ),
    [base, typeFilter],
  );

  const typeCounts = useMemo(() => {
    const scoped = base.filter(
      (m) => statusFilter === "all" || m.displayStatus === statusFilter,
    );
    return scoped.reduce<Record<string, number>>((acc, m) => {
      acc[m.type] = (acc[m.type] ?? 0) + 1;
      return acc;
    }, {});
  }, [base, statusFilter]);

  const rows = useMemo(
    () =>
      base
        .filter(
          (m) => statusFilter === "all" || m.displayStatus === statusFilter,
        )
        .filter((m) => typeFilter === "all" || m.type === typeFilter)
        .sort(
          (a, b) => meetingDateTime(b).valueOf() - meetingDateTime(a).valueOf(),
        ),
    [base, statusFilter, typeFilter],
  );

  // Clamp the page to the available range.
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = rows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const selected = selectedId
    ? (meetings.find((m) => m.id === selectedId) ?? null)
    : null;

  const handleConfirm = async (id: string, input: ConfirmInput) => {
    const result = await confirm(id, input);
    if (!result.ok) {
      showSnack(result.error ?? t("snack.confirm_failed"), "error");
      return;
    }
    showSnack(t("snack.confirmed"), "success");
    setSelectedId(null);
  };

  const handleCancel = async (id: string, note?: string) => {
    const result = await cancel(id, note);
    if (!result.ok) {
      showSnack(result.error ?? t("snack.cancel_failed"), "error");
      return;
    }
    showSnack(t("snack.cancelled"), "success");
    setSelectedId(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingMeeting) {
      return;
    }
    const target = deletingMeeting;
    setDeletingMeeting(null);
    const result = await remove(target.id);
    if (!result.ok) {
      showSnack(result.error ?? t("snack.delete_failed"), "error");
      return;
    }
    setSelectedId((prev) => (prev === target.id ? null : prev));
    showSnack(t("snack.deleted"), "success");
  };

  const resetTo =
    <T,>(setter: (v: T) => void) =>
    (v: T) => {
      setter(v);
      setPage(1);
    };

  return (
    <Box sx={{ display: "flex", height: "100%" }}>
      <MeetingsSidebar
        statusFilter={statusFilter}
        onStatusFilter={resetTo(setStatusFilter)}
        typeFilter={typeFilter}
        onTypeFilter={resetTo(setTypeFilter)}
        statusCounts={statusCounts}
        typeCounts={typeCounts}
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
      />

      <Box
        sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}
      >
        {!isVendor ? (
          <Box sx={{ p: 4 }}>
            <Typography variant="body1">{t("vendor_only")}</Typography>
          </Box>
        ) : selected ? (
          <MeetingDetail
            meeting={selected}
            availability={availability}
            bookings={activeBookings}
            leadCustomer={
              selected.customerEmail
                ? byEmail.get(selected.customerEmail)
                : undefined
            }
            onBack={() => setSelectedId(null)}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
          />
        ) : (
          <Box
            sx={{
              p: 2,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <MeetingsHeader onExport={() => exportMeetingsCsv(rows)} />

            <MeetingsToolbar
              search={search}
              dateFilter={dateFilter}
              onSearch={resetTo(setSearch)}
              onDateFilter={resetTo(setDateFilter)}
              onOpenFilters={() => setFiltersOpen(true)}
            />

            <Box sx={{ mb: 1.5 }}>
              <StatCards
                counts={statusCounts}
                selected={statusFilter}
                onSelect={resetTo(setStatusFilter)}
              />
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {pageRows.length === 0 ? (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    style={{ color: theme.palette.text.secondary }}
                  >
                    {t("no_matches")}
                  </Typography>
                </Box>
              ) : (
                <MeetingsTable
                  meetings={pageRows}
                  onView={(m) => setSelectedId(m.id)}
                  onConfirm={(m) => setSelectedId(m.id)}
                  onCancel={(m) => handleCancel(m.id)}
                  onDelete={(m) => setDeletingMeeting(m)}
                />
              )}
            </Box>

            <MeetingsPagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalRows={rows.length}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
              onPageSize={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </Box>
        )}
      </Box>

      {deletingMeeting && (
        <DeleteBookingDialog
          booking={deletingMeeting.booking}
          onCancel={() => setDeletingMeeting(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </Box>
  );
};

export default Meetings;
